import {
  type WorkspaceAgentMentionableItem,
  type WorkspaceAgentMentionItem,
  type WorkspaceAgentMentionMatchStrength,
  type WorkspaceAgentMentionRowItem,
  type WorkspaceAgentMentionSearchResult,
  type WorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { GeneralLibrary } from '~/utils/librariesUtils';

/** One shared instance stands in for "no library run", so a source without one is a stable reference. */
export const EMPTY_WORKSPACE_AGENT_MENTION_ITEMS: WorkspaceAgentMentionRowItem[] = [];

/** Boards and libraries nest; every other source is flat. */
export const isWorkspaceAgentMentionTreeItem = (
  item: WorkspaceAgentMentionItem,
): item is WorkspaceAgentMentionTreeItem => item.kind === 'board' || item.kind === 'library';

/**
 * The pillable set: not a library heading. Search ancestors stay in it — they are boards a pill can
 * still reference.
 */
export const isWorkspaceAgentMentionableItem = (
  item: WorkspaceAgentMentionItem,
): item is WorkspaceAgentMentionableItem => item.kind !== 'library';

/** First row a pill can come from; 0 when the list is empty or headings-only. */
export const getFirstWorkspaceAgentMentionableIndex = (items: readonly WorkspaceAgentMentionItem[]) => {
  const index = items.findIndex(isWorkspaceAgentMentionableItem);

  return index === -1 ? 0 : index;
};

const isWorkspaceAgentMentionHit = (item: WorkspaceAgentMentionItem) =>
  isWorkspaceAgentMentionableItem(item) && (!isWorkspaceAgentMentionTreeItem(item) || item.isMatch !== false);

/** First hit under a query; skips library headings and ancestors drawn only to place a match. */
export const getFirstWorkspaceAgentMentionHitIndex = (items: readonly WorkspaceAgentMentionItem[]) => {
  const index = items.findIndex(isWorkspaceAgentMentionHit);

  return index === -1 ? 0 : index;
};

/**
 * Hits for the live region, empty state, and loading gate only — not insert. Libraries are scenery;
 * a search ancestor is path to a hit rather than a hit.
 */
export const countWorkspaceAgentMentionHits = (items: WorkspaceAgentMentionItem[]) =>
  items.filter(isWorkspaceAgentMentionHit).length;

/** Ordered so follow-the-hits can move to a strictly stronger source without naming one. */
export const WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK: Record<WorkspaceAgentMentionMatchStrength, number> = {
  named: 2,
  none: 0,
  related: 1,
};

/**
 * What follow-the-hits reads. A source only has to land on `matchStrength`; the switcher does not
 * grow a case when a new pill type is registered.
 */
export type WorkspaceAgentMentionFollowHitsResult = Pick<
  WorkspaceAgentMentionSearchResult,
  'hasLooked' | 'isError' | 'matchStrength'
>;

/**
 * Title-containing hits are `named`; anything the source still returned is `related`. Empty query is
 * `none` — browse recents are not a match the picker should follow.
 */
export const getWorkspaceAgentMentionMatchStrength = (
  items: readonly WorkspaceAgentMentionItem[],
  query: string,
): WorkspaceAgentMentionMatchStrength => {
  const normalizedQuery = query.trim().toLowerCase();

  if (normalizedQuery === '') {
    return 'none';
  }

  const hits = items.filter(isWorkspaceAgentMentionHit);

  if (hits.length === 0) {
    return 'none';
  }

  return hits.some((item) => item.title.toLowerCase().includes(normalizedQuery)) ? 'named' : 'related';
};

/**
 * Next source whose settled answer is strictly stronger than the one on screen. Registry order only
 * breaks the scan — ties stay put, so a later source with the same strength cannot steal the tab.
 * `hasLooked` is the settle gate: a background refetch must not block a comparison the query already
 * answered.
 */
export const getWorkspaceAgentMentionSourceToFollow = <TKey extends string>(
  activeSourceKey: TKey,
  resultsBySource: Record<TKey, WorkspaceAgentMentionFollowHitsResult>,
  sourceKeys: readonly TKey[],
): TKey | undefined => {
  const activeResult = resultsBySource[activeSourceKey];

  if (!activeResult.hasLooked || activeResult.isError) {
    return undefined;
  }

  const activeRank = WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK[activeResult.matchStrength];

  return sourceKeys.find((sourceKey) => {
    if (sourceKey === activeSourceKey) {
      return false;
    }

    const result = resultsBySource[sourceKey];

    return (
      result.hasLooked &&
      !result.isError &&
      WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK[result.matchStrength] > activeRank
    );
  });
};

/** Index of the last descendant under `parentIndex`, or the parent itself when it has none. */
export const getWorkspaceAgentMentionSubtreeEndIndex = (
  items: readonly WorkspaceAgentMentionRowItem[],
  parentIndex: number,
): number => {
  const parent = items[parentIndex];

  if (!parent || !isWorkspaceAgentMentionTreeItem(parent)) {
    return parentIndex;
  }

  let endIndex = parentIndex;

  for (let index = parentIndex + 1; index < items.length; index += 1) {
    const item = items[index];

    if (!item || !isWorkspaceAgentMentionTreeItem(item) || item.depth <= parent.depth) {
      break;
    }

    endIndex = index;
  }

  return endIndex;
};

/**
 * Last row to hang a next-page sentinel off. Missing parent is only valid for Global when those
 * roots are drawn as depth-0 boards — the paging node is not itself a row then.
 */
const getWorkspaceAgentMentionSentinelEndIndex = (
  items: readonly WorkspaceAgentMentionRowItem[],
  nodeId: string,
): number => {
  const parentIndex = items.findIndex((item) => item.id === nodeId);

  if (parentIndex !== -1) {
    return getWorkspaceAgentMentionSubtreeEndIndex(items, parentIndex);
  }

  if (nodeId !== GeneralLibrary.id || items.length === 0) {
    return -1;
  }

  return items.length - 1;
};

/** Where to mount each parent's next-page sentinel: after that parent's last visible descendant. */
export const toWorkspaceAgentMentionSentinelEndIndexes = (
  items: readonly WorkspaceAgentMentionRowItem[],
  hasNextPageNodeIds: ReadonlySet<string>,
): ReadonlyMap<number, readonly string[]> => {
  const nodeIdsByEndIndex = new Map<number, string[]>();

  hasNextPageNodeIds.forEach((nodeId) => {
    const endIndex = getWorkspaceAgentMentionSentinelEndIndex(items, nodeId);

    if (endIndex === -1) {
      return;
    }

    const nodeIds = nodeIdsByEndIndex.get(endIndex);

    if (nodeIds) {
      nodeIds.push(nodeId);
    } else {
      nodeIdsByEndIndex.set(endIndex, [nodeId]);
    }
  });

  return nodeIdsByEndIndex;
};

export type WorkspaceAgentMentionLibraryRun = {
  /** Where the library run starts, so the rows inside it keep the whole list's indexes. */
  firstLibraryIndex: number;
  /**
   * Rows before the first library heading. With named libraries Global heads that run, so this is
   * empty then. With none, every board is leading — there is no Libraries group.
   */
  leadingItems: WorkspaceAgentMentionRowItem[];
  libraryRunItems: WorkspaceAgentMentionRowItem[];
};

/**
 * The tree always ends with the libraries and everything under them. The run needs a group of its
 * own, which is why it is separated at all.
 */
export const splitWorkspaceAgentMentionLibraryRun = (
  items: WorkspaceAgentMentionRowItem[],
): WorkspaceAgentMentionLibraryRun => {
  const firstLibraryIndex = items.findIndex((item) => item.kind === 'library');

  if (firstLibraryIndex === -1) {
    return { firstLibraryIndex, leadingItems: items, libraryRunItems: EMPTY_WORKSPACE_AGENT_MENTION_ITEMS };
  }

  return {
    firstLibraryIndex,
    leadingItems: items.slice(0, firstLibraryIndex),
    libraryRunItems: items.slice(firstLibraryIndex),
  };
};
